import * as React from "react";
import { useState } from "react";
import { useSocialCoachSession } from "@/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Table, Button, Badge, Spinner, confirmDialog } from "@/components/ui";
import { Pencil, Trash2 } from "lucide-react";
import { VideoPageThemeForm } from "./components/VideoPageThemeForm";
import { VideoPageThemeDefaultsEditor } from "./components/VideoPageThemeDefaultsEditor";
import { EqualHousingLenderLogo, EqualHousingOpportunityLogo } from "./components/EqualHousingLogos";
import { ContentPageThemeServices } from "@/services/bedrock/ContentPageThemeServices";
import { ContentPageTheme, ContentPageThemeDefault } from "@/services/bedrock";
import { toast } from "sonner";

const ensureHashPrefix = (color: string | undefined, defaultColor: string = "") => {
  if (!color) return defaultColor;
  return color.startsWith("#") ? color : `#${color}`;
};

export const VideoPageThemes: React.FC = () => {
  const queryClient = useQueryClient();
  const { playerId, coachId } = useSocialCoachSession();
  const [selectedTheme, setSelectedTheme] = useState<ContentPageTheme | undefined>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [openDefaultsModal, setOpenDefaultsModal] = useState<boolean>(false);

  // Queries
  const { data: themes = [], isLoading: isLoadingThemes } = useQuery<ContentPageTheme[]>({
    queryKey: ["contentPageThemes"],
    queryFn: async () => {
      const response = await ContentPageThemeServices.getPageThemes();
      return response.content || [];
    },
  });

  const { data: themeDefaults = [], isLoading: isLoadingDefaults } = useQuery<ContentPageThemeDefault[]>({
    queryKey: ["contentPageThemeDefaults"],
    queryFn: async () => {
      const response = await ContentPageThemeServices.getPageThemeDefaults(1, 1000);
      return response.content || [];
    },
  });

  // Mutations
  const makeDefaultMutation = useMutation({
    mutationFn: async (theme: ContentPageTheme) => {
      return await ContentPageThemeServices.updatePageTheme(theme.id!, { makeDefault: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contentPageThemes"] });
      queryClient.invalidateQueries({ queryKey: ["contentPageThemeDefaults"] });
      toast.success("Theme set as default!");
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to set theme as default.");
    },
  });

  const deleteThemeMutation = useMutation({
    mutationFn: async (theme: ContentPageTheme) => {
      return await ContentPageThemeServices.deletePageTheme(theme.id!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contentPageThemes"] });
      queryClient.invalidateQueries({ queryKey: ["contentPageThemeDefaults"] });
      toast.success("Theme removed successfully.");
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to remove theme.");
    },
  });

  const loading = isLoadingThemes || isLoadingDefaults;

  const editTheme = (theme: ContentPageTheme, asCopy: boolean = false) => {
    setSelectedTheme({ ...theme, id: asCopy ? -1 : theme.id });
    setEditMode(true);
  };

  const makeDefault = (theme: ContentPageTheme) => {
    makeDefaultMutation.mutate(theme);
  };

  const archiveTheme = async (theme: ContentPageTheme) => {
    const confirmed = await confirmDialog({
      title: "Remove Theme",
      description: "Are you sure you want to remove this theme?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (confirmed) {
      deleteThemeMutation.mutate(theme);
    }
  };

  const canEdit = (theme: ContentPageTheme) => {
    return theme.coachId != -1 && ((playerId && theme.playerId == playerId) || (!playerId && theme.coachId == coachId));
  };

  const isDefault = (themeId: number | undefined) => {
    return themeDefaults.some(td => td.themeId == themeId && !td.segmentationDetailId);
  };

  return (
    <div className="relative min-h-[350px] w-full text-left">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-50">
          <Spinner className="size-8" />
        </div>
      )}

      {!loading && !editMode && (
        <div className="space-y-4">
          <div className="flex justify-end items-center gap-2">
            {!playerId && (
              <Button
                variant="outline"
                onClick={() => {
                  setOpenDefaultsModal(true);
                }}
              >
                Manage Region/Branch Defaults
              </Button>
            )}
            <Button
              onClick={() => {
                setSelectedTheme(undefined);
                setEditMode(true);
              }}
            >
              New Theme
            </Button>
          </div>

          <div className="border border-border rounded-lg overflow-hidden bg-background">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head className="w-[15%]">Theme</Table.Head>
                  <Table.Head className="w-[15%]">Theme Preview</Table.Head>
                  <Table.Head className="w-[12%]">Header Logo</Table.Head>
                  <Table.Head className="w-[20%]">Footer Logos</Table.Head>
                  <Table.Head className="w-[20%]">Disclaimer</Table.Head>
                  <Table.Head className="w-[10%]">Owner</Table.Head>
                  <Table.Head className="text-right w-[8%]">Actions</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {themes.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No themes found.
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  themes.map(theme => (
                    <Table.Row key={theme.id} className="hover:bg-muted/30">
                      <Table.Cell className="align-middle font-medium">
                        <div className="flex flex-col gap-1.5">
                          <span>{theme.name}</span>
                          {isDefault(theme.id) && (
                            <div className="flex">
                              <Badge
                                variant="outline"
                                className="bg-muted text-foreground border-border text-xs px-2 py-0.5"
                              >
                                Default
                              </Badge>
                            </div>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="align-middle min-w-[200px]">
                        <div
                          className="border border-border/80 w-full max-w-[220px] h-[100px] flex flex-col items-center justify-center p-3 rounded-lg text-sm font-semibold shadow-sm"
                          style={{
                            backgroundColor: ensureHashPrefix(theme.backgroundColor),
                            color: ensureHashPrefix(theme.textColor),
                          }}
                        >
                          Text Color
                          <div
                            className="mt-2 px-4 py-1.5 border-2 rounded-full text-center text-xs font-bold"
                            style={{
                              backgroundColor: ensureHashPrefix(theme.primaryCtaColor),
                              color: ensureHashPrefix(theme.primaryCtaTextColor),
                              borderColor: ensureHashPrefix(theme.primaryCtaTextColor),
                            }}
                          >
                            Call To Action
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="align-middle">
                        {theme.headerMedia?.[0]?.uri ? (
                          <img
                            src={theme.headerMedia[0].uri}
                            alt="Header Logo"
                            className="max-h-10 max-w-[100px] object-contain rounded border border-border bg-muted/25 p-1"
                          />
                        ) : (
                          <span className="text-muted-foreground text-xs italic">No Logo</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="align-middle">
                        <div className="flex flex-wrap gap-2 items-center">
                          {theme.showEhLenderLogo && (
                            <div
                              className="p-1 rounded border border-border/40 inline-flex max-w-max"
                              style={{ backgroundColor: ensureHashPrefix(theme.backgroundColor) }}
                            >
                              <EqualHousingLenderLogo
                                textColor={ensureHashPrefix(theme.textColor)}
                                className="h-4.5 w-auto shrink-0"
                              />
                            </div>
                          )}
                          {theme.showEhOpportunityLogo && (
                            <div
                              className="p-1 rounded border border-border/40 inline-flex max-w-max bg-muted/20"
                              style={{ backgroundColor: ensureHashPrefix(theme.backgroundColor) }}
                            >
                              <EqualHousingOpportunityLogo
                                textColor={ensureHashPrefix(theme.textColor)}
                                className="h-4.5 w-auto shrink-0"
                              />
                            </div>
                          )}
                          {(theme.footerMedia || []).map((mediaItem, index) => (
                            <img
                              key={`table-footer-logo-${index}`}
                              src={mediaItem.uri}
                              alt="Footer Logo"
                              className="h-6.5 w-auto max-w-[60px] object-contain rounded border border-border bg-muted/20 p-0.5"
                            />
                          ))}
                          {!theme.showEhLenderLogo &&
                            !theme.showEhOpportunityLogo &&
                            (!theme.footerMedia || theme.footerMedia.length === 0) && (
                              <span className="text-muted-foreground text-xs italic">None</span>
                            )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="align-middle">
                        {theme.disclaimer ? (
                          <div
                            className="max-w-[200px] line-clamp-2 text-xs text-muted-foreground font-medium"
                            title={theme.disclaimer}
                          >
                            {theme.disclaimer}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs italic">None</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-sm text-muted-foreground font-medium">
                        {theme.coachId == -1 ? (
                          <Badge
                            variant="secondary"
                            className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-none whitespace-nowrap"
                          >
                            SocialCoach
                          </Badge>
                        ) : theme.playerId ? (
                          <Badge
                            variant="secondary"
                            className="bg-blue-50 text-blue-700 hover:bg-blue-50 border-none whitespace-nowrap"
                          >
                            You
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-none whitespace-nowrap"
                          >
                            Your Account
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-right">
                        <div className="flex justify-end gap-1.5">
                          {!canEdit(theme) && (
                            <Button variant="outline" size="sm" onClick={() => editTheme(theme, true)}>
                              Make A Copy
                            </Button>
                          )}
                          {canEdit(theme) && (
                            <>
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8 rounded-lg"
                                onClick={() => editTheme(theme)}
                                title="Edit Theme"
                              >
                                <Pencil className="size-4" />
                              </Button>
                              <Button
                                variant="destructiveGhost"
                                size="icon"
                                className="size-8 rounded-lg"
                                onClick={() => archiveTheme(theme)}
                                loading={
                                  deleteThemeMutation.isPending && deleteThemeMutation.variables?.id === theme.id
                                }
                                title="Delete Theme"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => makeDefault(theme)}
                                disabled={
                                  isDefault(theme.id) ||
                                  (makeDefaultMutation.isPending && makeDefaultMutation.variables?.id === theme.id)
                                }
                                loading={
                                  makeDefaultMutation.isPending && makeDefaultMutation.variables?.id === theme.id
                                }
                              >
                                Make Default
                              </Button>
                            </>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      )}

      {!loading && editMode && (
        <VideoPageThemeForm
          selectedTheme={selectedTheme}
          onUpdate={(_: ContentPageTheme) => {
            queryClient.invalidateQueries({ queryKey: ["contentPageThemes"] });
            setEditMode(false);
          }}
          goBack={() => {
            setEditMode(false);
          }}
        />
      )}

      {!playerId && (
        <VideoPageThemeDefaultsEditor
          open={openDefaultsModal}
          loading={loading}
          themes={themes}
          defaults={themeDefaults}
          onClose={() => setOpenDefaultsModal(false)}
          onSaved={newDefaults => {
            queryClient.setQueryData(["contentPageThemeDefaults"], newDefaults);
            setOpenDefaultsModal(false);
            toast.success("Theme defaults saved!");
          }}
        />
      )}
    </div>
  );
};
